import React, { useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { OrderOption, OrderOptions } from '../../api/types';
import * as S from './BookmarkFilters.styled';
import { BookmarkOrderSelect } from './BookmarkOrderSelect';
import { CategoriesLoader } from './CategoriesLoader';
import {
  DEFAULT_ID,
  SCROLL_STEP,
  SCROLL_GAP,
  SCROLLER_OFFSET,
  SCROLL_THRESHOLD,
} from './BookmarkFilters.constants';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { BookmarkedCategory } from '@/api/types/user.types';
import { ChevronDown, SortIcon } from '@/components/UI/Icons/Icons';
import { useScroller } from '@/hooks/useScroller';

interface BookmarkFiltersProps {
  loading: boolean;
  orderBy: OrderOption;
  totalAdsCount?: number;
  categories: BookmarkedCategory[];
  onOrderChange: (order: OrderOption) => void;
  onCategoryChange: (categoryId: string | null) => void;
}

export const BookmarkFilters = ({
  loading,
  orderBy,
  categories,
  totalAdsCount,
  onOrderChange,
  onCategoryChange,
}: BookmarkFiltersProps) => {
  const { t } = useTranslation();
  const { isDesktop } = useBreakpoint();
  const orderButtonRef = useRef<HTMLButtonElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [active, setActive] = useState('');
  const [isSortingDropdownOpen, setIsSortingDropdownOpen] = useState(false);
  const { isScrolled, isOverflowing, onScrollerClick } = useScroller(
    {
      containerRef: scrollContainerRef,
      step: SCROLL_STEP,
      threshold: SCROLL_THRESHOLD,
      gap: SCROLL_GAP,
    },
    [categories, isDesktop]
  );

  const allLabel = isDesktop
    ? 'bookmark_collections.default_category'
    : 'bookmark_collections.default_category_mobile';
  const hasCategories = categories.length > 0;
  const hasScroller = !loading && isDesktop && isOverflowing;
  const orderButtonWidth = orderButtonRef.current?.offsetWidth ?? 0;
  const scrollerOffset = orderButtonWidth + SCROLLER_OFFSET;

  const sortedCategories = useMemo(() => {
    return [...categories].sort(
      (a, b) => b.bookmarkedAdsCount - a.bookmarkedAdsCount
    );
  }, [categories]);

  const handleCategoryClick = (item?: BookmarkedCategory) => {
    const categoryId = item?.category?.id ?? '';

    setActive(categoryId);
    onCategoryChange(categoryId);

    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const dataId = categoryId || DEFAULT_ID;
      const activeElement = container.querySelector(`[data-id="${dataId}"]`);

      if (activeElement) {
        activeElement.scrollIntoView({
          block: 'nearest',
          inline: 'center',
          behavior: 'smooth',
        });
      }
    }
  };

  const handleOrderChange = (order: OrderOption) => {
    onOrderChange(order);
    setIsSortingDropdownOpen(false);
  };

  const orderButtonTitle = useMemo(() => {
    onCategoryChange(active || '');

    if (orderBy === OrderOptions.CREATED_AT_DESC) {
      return t('bookmark_collections.sort_by_publish_date');
    }

    if (orderBy === OrderOptions.PRICE_DESC) {
      return t('bookmark_collections.sort_by_price_desc');
    }

    if (orderBy === OrderOptions.PRICE_ASC) {
      return t('bookmark_collections.sort_by_price_asc');
    }

    return t('bookmark_collections.sort_button_title');
  }, [orderBy]);

  const renderDropdownButton = () => {
    if (!(loading || hasCategories)) return null;

    return (
      <S.OrderButton
        ref={orderButtonRef}
        $isMinimized={!isDesktop}
        $isActive={isSortingDropdownOpen}
        onClick={() => setIsSortingDropdownOpen(true)}
      >
        <SortIcon />
        {isDesktop && (
          <>
            {orderButtonTitle}
            <S.DropdownIcon />
          </>
        )}
        <BookmarkOrderSelect
          value={orderBy}
          isOpen={isSortingDropdownOpen}
          onChange={handleOrderChange}
          onClose={() => setIsSortingDropdownOpen(false)}
        />
      </S.OrderButton>
    );
  };

  if (!loading && !categories.length) return null;

  return (
    <S.Container>
      {hasScroller && isScrolled && (
        <S.ScrollerButton $isReversed onClick={onScrollerClick}>
          <ChevronDown />
        </S.ScrollerButton>
      )}

      <S.CategoriesList ref={scrollContainerRef}>
        {loading ? (
          <CategoriesLoader />
        ) : (
          <>
            {hasCategories && (
              <S.CategoryItem
                $isActive={!active}
                data-id={DEFAULT_ID}
                onClick={() => handleCategoryClick()}
              >
                {t(allLabel, { count: totalAdsCount })}
              </S.CategoryItem>
            )}

            {sortedCategories.map(item => {
              const isActive = active === item.category.id;

              return (
                <S.CategoryItem
                  $isActive={isActive}
                  key={item.category.id}
                  data-id={item.category.id}
                  onClick={() => handleCategoryClick(item)}
                >
                  {item.category.name} {item.bookmarkedAdsCount}
                </S.CategoryItem>
              );
            })}
          </>
        )}
      </S.CategoriesList>

      {hasScroller && !isScrolled && (
        <S.ScrollerButton
          $rightOffset={scrollerOffset}
          onClick={onScrollerClick}
        >
          <ChevronDown />
        </S.ScrollerButton>
      )}

      {renderDropdownButton()}
    </S.Container>
  );
};
